import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { CreateDisplayAdDialog } from '@bingads-webui/inline-copilot-ui';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';

import { getAdsInMatchingGroup, getItemsForListFromGroupedAds } from '../model/constants';

export const CreateAdWithAiDialog = ({
  isOpen,
  onDismiss,
  onFinalURLFocusOut,
  onCreateAds,
}) => {
  const activity = useContext(ActivityContext);

  const [{ isLoading, groupedAds } = {}] = useContext(RecommendationContext);
  const { finalUrl: initialFinalUrl = '' } = useCopilotRepositoryContext();

  const [finalUrl, setFinalUrl] = useState(initialFinalUrl);
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  const handleFinalUrlUpdate = useCallback((url) => {
    if (url === finalUrl) { return; }

    if (activity) {
      activity.signal('[Create Ad with AI dialog] - Updating Final URL');
    }

    setFinalUrl(url);
    setImages([]);
    setSelectedImages([]);

    onFinalURLFocusOut(url);
  }, [finalUrl, onFinalURLFocusOut, activity]);

  useEffect(() => {
    setImages(getItemsForListFromGroupedAds(groupedAds));
  }, [groupedAds]);

  const handleSelectImages = useCallback((ids) => {
    if (activity) {
      // ids and selectedImage should differ by one element, the last the user clicked
      const lastId = _.difference(_.union(ids, selectedImages), _.intersection(ids, selectedImages))[0];

      if (ids.includes(lastId)) {
        activity.signal(`[Create Ad with AI dialog] - Selecting image ${lastId}`);
      } else {
        activity.signal(`[Create Ad with AI dialog] - Deselecting image ${lastId}`);
      }
    }

    setSelectedImages(ids);
  }, [selectedImages, activity]);

  const handleCreateAds = useCallback((ids) => {
    if (activity) {
      activity.signal('[Create Ad with AI dialog] - Creating ads');
    }

    const matchingAds = _.chain(ids)
      .map(id => getAdsInMatchingGroup(groupedAds, id))
      .flatten()
      .value();

    return onCreateAds(matchingAds).then(onDismiss);
  }, [groupedAds, onCreateAds, onDismiss, activity]);

  const handleDismiss = useCallback(() => {
    if (activity) {
      activity.signal('[Create Ad with AI dialog] - Closing dialog');
    }

    onDismiss();
  }, [onDismiss, activity]);

  return (
    <div>
      <CreateDisplayAdDialog
        finalUrl={finalUrl}
        images={images}
        selectedImages={selectedImages}
        isOpen={isOpen}
        isLoading={isLoading}
        onCreateAds={handleCreateAds}
        onDismiss={handleDismiss}
        onFinalUrlUpdate={handleFinalUrlUpdate}
        onSelectedImagesUpdate={handleSelectImages}
      />
    </div>
  );
};

CreateAdWithAiDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onFinalURLFocusOut: PropTypes.func.isRequired,
  onCreateAds: PropTypes.func.isRequired,
};
